/*
 * Code generated by Speakeasy (https://speakeasy.com). DO NOT EDIT.
 */

import {
  InvalidateQueryFilters,
  QueryClient,
  useQuery,
  UseQueryResult,
  useSuspenseQuery,
  UseSuspenseQueryResult,
} from "@tanstack/react-query";
import { GramError } from "../models/errors/gramerror.js";
import {
  ConnectionError,
  InvalidRequestError,
  RequestAbortedError,
  RequestTimeoutError,
  UnexpectedClientError,
} from "../models/errors/httpclienterrors.js";
import { ResponseValidationError } from "../models/errors/responsevalidationerror.js";
import { SDKValidationError } from "../models/errors/sdkvalidationerror.js";
import { ServiceError } from "../models/errors/serviceerror.js";
import {
  ListAdminOpenRouterKeysRequest,
  ListAdminOpenRouterKeysSecurity,
} from "../models/operations/listadminopenrouterkeys.js";
import { useGramContext } from "./_context.js";
import {
  QueryHookOptions,
  SuspenseQueryHookOptions,
  TupleToPrefixes,
} from "./_types.js";
import {
  AdminOpenRouterKeysQueryData,
  buildAdminOpenRouterKeysQuery,
  prefetchAdminOpenRouterKeys,
  queryKeyAdminOpenRouterKeys,
} from "./adminOpenRouterKeys.core.js";
export {
  type AdminOpenRouterKeysQueryData,
  buildAdminOpenRouterKeysQuery,
  prefetchAdminOpenRouterKeys,
  queryKeyAdminOpenRouterKeys,
};

export type AdminOpenRouterKeysQueryError =
  | ServiceError
  | GramError
  | ResponseValidationError
  | ConnectionError
  | RequestAbortedError
  | RequestTimeoutError
  | InvalidRequestError
  | UnexpectedClientError
  | SDKValidationError;

/**
 * listKeys adminOpenRouterKeys
 *
 * @remarks
 * List every organization's platform OpenRouter keys. Requires platform admin.
 */
export function useAdminOpenRouterKeys(
  request?: ListAdminOpenRouterKeysRequest | undefined,
  security?: ListAdminOpenRouterKeysSecurity | undefined,
  options?: QueryHookOptions<
    AdminOpenRouterKeysQueryData,
    AdminOpenRouterKeysQueryError
  >,
): UseQueryResult<AdminOpenRouterKeysQueryData, AdminOpenRouterKeysQueryError> {
  const client = useGramContext();
  return useQuery({
    ...buildAdminOpenRouterKeysQuery(
      client,
      request,
      security,
      options,
    ),
    ...options,
  });
}

/**
 * listKeys adminOpenRouterKeys
 *
 * @remarks
 * List every organization's platform OpenRouter keys. Requires platform admin.
 */
export function useAdminOpenRouterKeysSuspense(
  request?: ListAdminOpenRouterKeysRequest | undefined,
  security?: ListAdminOpenRouterKeysSecurity | undefined,
  options?: SuspenseQueryHookOptions<
    AdminOpenRouterKeysQueryData,
    AdminOpenRouterKeysQueryError
  >,
): UseSuspenseQueryResult<
  AdminOpenRouterKeysQueryData,
  AdminOpenRouterKeysQueryError
> {
  const client = useGramContext();
  return useSuspenseQuery({
    ...buildAdminOpenRouterKeysQuery(
      client,
      request,
      security,
      options,
    ),
    ...options,
  });
}

export function setAdminOpenRouterKeysData(
  client: QueryClient,
  queryKeyBase: [parameters: { gramSession?: string | undefined }],
  data: AdminOpenRouterKeysQueryData,
): AdminOpenRouterKeysQueryData | undefined {
  const key = queryKeyAdminOpenRouterKeys(...queryKeyBase);

  return client.setQueryData<AdminOpenRouterKeysQueryData>(key, data);
}

export function invalidateAdminOpenRouterKeys(
  client: QueryClient,
  queryKeyBase: TupleToPrefixes<
    [parameters: { gramSession?: string | undefined }]
  >,
  filters?: Omit<InvalidateQueryFilters, "queryKey" | "predicate" | "exact">,
): Promise<void> {
  return client.invalidateQueries({
    ...filters,
    queryKey: [
      "@gram/client",
      "adminOpenRouterKeys",
      "listKeys",
      ...queryKeyBase,
    ],
  });
}

export function invalidateAllAdminOpenRouterKeys(
  client: QueryClient,
  filters?: Omit<InvalidateQueryFilters, "queryKey" | "predicate" | "exact">,
): Promise<void> {
  return client.invalidateQueries({
    ...filters,
    queryKey: ["@gram/client", "adminOpenRouterKeys", "listKeys"],
  });
}
